import {BaseFactory} from "../../../infrastructure/factories/base-factory";
import {StandingsResponseApiModel} from "../models/standings.response-api-model";
import {StandingsApiModel} from "../models/standings.api-model";
import {StandingsDomainModel} from "../models/standings.domain-model";
import {CompetitorDomainModel} from "../../common/competitor/models/competitor-domain-model";
import {GroupApiModel} from "../../common/group/models/group-api-model";
import {CountryApiModel} from "../../common/country/models/country-api-model";
import {CompetitionDomainModel} from "../../common/competition/models/competition-domain-model";
import {StandingsRowDomainModel} from "../models/row/standings.row-domain-model";
import {StandingsRowApiModel} from "../models/row/standings.row-api-model";
import {SportApiModel} from "../../sports/models/sport-api-model";
import {CompetitorFactory} from "../../common/competitor/domain-services/competitor-factory";
import {CompetitionFactory} from "../../common/competition/domain-services/competition-factory";
import {CountryDomainModel} from "../../common/country/models/country-domain-model";
import {CountryFactory} from "../../common/country/domain-services/country-factory";
import {SportsFactory} from "../../sports/domain-services/sports.factory";
import {SportDomainModel} from "../../sports/models/sport-domain-model";
import {LoggerUtils} from "../../../infrastructure/utils/logger/logger-utils";
import {TermsUtils} from "../../../infrastructure/utils/terms-utils";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {PartialGameFactory} from "../../partial-game/services/partial-game.factory";
import {PartialGameApiModel} from "../../partial-game/models/partial-game-api-model";
import {PartialGameDomainModel} from "../../partial-game/models/partial-game-domain-model";
import {StandingsDataSource} from "../models/standings-data-source";
import {BaseApiRequestModel} from "../../../infrastructure/models/base-api-request-model";
import {StandingsDestinationDomainModel} from "../models/destinations/standings.destinations.domain-model";
import {StandingsDestinationApiModel} from "../models/destinations/standings.destinations.api-model";
import {BracketsDomainModel} from "../../brackets/models/brackets.domain-model";
import {BracketsApiModel} from "../../brackets/models/brackets.api-model";
import {GroupDomainModel} from "../../common/group/models/group.domain-model";
import {GameCompetitorDomainModel} from "../../partial-game/models/game-competitor/game-competitor-domain-model";
import {StandingsSeasonsFilterDomainModel} from "../models/seasons-filter/standings.seasons-filter.domain-model";

export class StandingsFactory extends BaseFactory {
  sportFactory: SportsFactory;
  countryFactory: CountryFactory;
  competitorFactory: CompetitorFactory;
  competitionFactory: CompetitionFactory;
  partialGameFactory: PartialGameFactory;
  datetimeUtils: DatetimeUtils;

  static injectables = [...BaseFactory.injectables, CompetitionFactory, CompetitorFactory, CountryFactory, SportsFactory, PartialGameFactory, DatetimeUtils];

  constructor(loggerUtils: LoggerUtils, termsUtils: TermsUtils, competitionFactory: CompetitionFactory, competitorFactory: CompetitorFactory, countryFactory: CountryFactory, sportFactory: SportsFactory, partialGameFactory: PartialGameFactory, datetimeUtils: DatetimeUtils) {
    super(loggerUtils, termsUtils);

    this.sportFactory = sportFactory;
    this.countryFactory = countryFactory;
    this.competitorFactory = competitorFactory;
    this.competitionFactory = competitionFactory;
    this.partialGameFactory = partialGameFactory;
    this.datetimeUtils = datetimeUtils;
  }


  createResponse(apiModel: StandingsResponseApiModel, request: BaseApiRequestModel) {
    const responseApiModel: StandingsResponseApiModel = new StandingsResponseApiModel(apiModel, request);

    return responseApiModel;
  }

  createStandingsRowsDomainModel(standingApiModel: StandingsApiModel, countriesMap: Map<Number, CountryApiModel>, competitionDomainModel: CompetitionDomainModel, games: Array<PartialGameDomainModel>, sports: Array<SportDomainModel>): Array<StandingsRowDomainModel> {
    const standingsRowsDomainModel = standingApiModel.rows.map((standingRowApiModel: StandingsRowApiModel) => {
      const game: PartialGameDomainModel = games.find((game: PartialGameDomainModel) => game.id === standingRowApiModel.liveGameId);
      const destinationDomainModel: StandingsDestinationApiModel = standingApiModel.destinations && standingApiModel.destinations.find((destination: StandingsDestinationApiModel) => destination.num === standingRowApiModel.destinationNum);
      const groupApiModel: GroupApiModel = standingApiModel.groups?.find((group: GroupApiModel) => group.num === standingRowApiModel.groupNum);
      const countryDomainModel: CountryDomainModel = countriesMap.get(standingRowApiModel.competitor.countryId)
      const competitorDomainModel: CompetitorDomainModel = new CompetitorDomainModel(standingRowApiModel.competitor, countryDomainModel, competitionDomainModel.sport);
      const detailedRecentFormDomainModel: Array<PartialGameDomainModel> = standingRowApiModel.detailedRecentForm && standingRowApiModel.detailedRecentForm.map((partialGameApiModel: PartialGameApiModel) => this.partialGameFactory.createPartialGameDomainObject(partialGameApiModel, competitionDomainModel, countriesMap, competitionDomainModel.sport))?.removeNulls();
      const rowDomain: StandingsRowDomainModel = this.createStandingsRowDomainObject(standingRowApiModel, competitorDomainModel, destinationDomainModel, detailedRecentFormDomainModel, groupApiModel, game, competitionDomainModel, sports.first(), countriesMap);

      return rowDomain;
    });

    return standingsRowsDomainModel;
  }

  createDomainModel(response: StandingsResponseApiModel): StandingsDataSource {
    const countriesMap: Map<Number, CountryDomainModel> = this.countryFactory.createDomainMap(response.countries, CountryDomainModel);
    const sportsMap: Map<Number, SportDomainModel> = this.sportFactory.createDomainMap(response.sports, SportDomainModel);
    const competitionsMap: Map<Number, CompetitionDomainModel> = this.competitionFactory.createDomainMap(response.competitions, CompetitionDomainModel, 'id', response.sports, response.countries);

    const games: Array<PartialGameDomainModel> = (response.games || []).map((game: PartialGameApiModel) => {
      const competition: CompetitionDomainModel = competitionsMap.get(game.competitionId);
      const sport: SportApiModel = sportsMap.get(game.sportId);

      const gameDomain: PartialGameDomainModel = this.partialGameFactory.createPartialGameDomainObject(game, competition, countriesMap, sport);

      return gameDomain;
    });


    const standings: Array<StandingsDomainModel> = response.standings?.map((standingApiModel: StandingsApiModel) => {
      const competitionDomainModel: CompetitionDomainModel = competitionsMap.get(standingApiModel.competitionId);
      const sports: Array<SportDomainModel> = response.sports?.map((sport: SportApiModel) => new SportDomainModel(sport));
      const rows: Array<StandingsRowDomainModel> = this.createStandingsRowsDomainModel(standingApiModel, countriesMap, competitionDomainModel, games, sports);
      const includeNextMatchColumn: Boolean = rows.filter((row: StandingsRowDomainModel) => row.nextMatch).length > 0;
      const standingsDomainModel: StandingsDomainModel = new StandingsDomainModel(standingApiModel, competitionDomainModel, rows, includeNextMatchColumn);

      return standingsDomainModel;
    });

    const brackets: Array<BracketsDomainModel> = response?.brackets?.map((bracketApiModel: BracketsApiModel) => this.createBracketsDomainModel(bracketApiModel, sportsMap, countriesMap, competitionsMap))
    const seasonsFilter = response.seasonsFilter?.map(filter => new StandingsSeasonsFilterDomainModel(filter));
    const standingsDataSource: StandingsDataSource = new StandingsDataSource(response, games, standings, brackets, Array.from(sportsMap.values()), Array.from(competitionsMap.values()), seasonsFilter);

    return standingsDataSource;
  }


  createStandingsRowDomainObject(apiModel: StandingsRowApiModel, competitor: CompetitorDomainModel, destination: StandingsDestinationDomainModel, detailedRecentFormDomainModel: Array<PartialGameDomainModel>, group: GroupApiModel, game: PartialGameDomainModel, competition: CompetitionDomainModel, sportDomainModel, countriesMap): StandingsRowDomainModel {
    const defaultGroup = {
      num: -1,
      name: 'defaultGroup'
    }
    const groupDomainModel = new GroupDomainModel(group || defaultGroup);
    const nextMatch: PartialGameDomainModel = apiModel.nextMatch ? this.createNextMatchDomainModel(apiModel, countriesMap, competition, sportDomainModel) : null
    const domain: StandingsRowDomainModel = new StandingsRowDomainModel(apiModel, competitor, destination, detailedRecentFormDomainModel, groupDomainModel, game, nextMatch);

    return domain;
  }

  createNextMatchDomainModel(apiModel, countriesMap, competition, sport): PartialGameDomainModel {
    const homeCompetitorCountryDomain: CountryDomainModel = countriesMap.get(apiModel.nextMatch.homeCompetitor?.countryId);
    const awayCompetitorCountryDomain: CountryDomainModel = countriesMap.get(apiModel.nextMatch.awayCompetitor?.countryId);
    const homeCompetitor: GameCompetitorDomainModel = this.partialGameFactory.createGameCompetitorDomainObject(apiModel.nextMatch.homeCompetitor, homeCompetitorCountryDomain, sport);
    const awayCompetitor: GameCompetitorDomainModel = this.partialGameFactory.createGameCompetitorDomainObject(apiModel.nextMatch.awayCompetitor, awayCompetitorCountryDomain, sport);
    const startTime: Date = this.datetimeUtils.fromISO(apiModel.nextMatch.startTime);
    const nextMatch: PartialGameDomainModel = apiModel.nextMatch && new PartialGameDomainModel(apiModel.nextMatch, competition, homeCompetitor, awayCompetitor, startTime, sport);

    return nextMatch;
  }

  updateApiResponseModel(response: StandingsResponseApiModel, delta: StandingsResponseApiModel) {
    this.updateApiModel(response, delta);

    if (delta?.standings) {
      response.games = delta.games;
      response.sports = delta.sports;
      response.countries = delta.countries;
      response.competitions = delta.competitions;
      response.bookmakers = delta.bookmakers;

      this.updateApiModelArray(response.standings, delta.standings, (res, del) => this.updateStandingsRowsApiModel(res, del));
    }

    return response;
  }

  updateStandingsRowsApiModel(response: StandingsApiModel, delta: StandingsApiModel) {
    if (!response || !delta) {
      return;
    }

    this.updateApiModelArray(response.rows, delta.rows, (...params) => this.updateApiModel(...params), (a,b) => a.competitor.id === b.competitor.id);
  }
}
